import { useContext, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';

import { isFilterableDataType } from '@atom/components/common/workOrdersFilters/fieldFilterUtilities';
import TaskFieldFilterInput from '@atom/components/common/workOrdersFilters/TaskFieldFilterInput';
import WorkOrdersContext, {
  getTaskField,
  WorkOrdersInputActionTypes,
} from '@atom/components/workOrders/WorkOrdersContext';
import { GET_WORK_ORDER_TEMPLATE } from '@atom/graph/workTemplate';
import { Icon, IconButton } from '@atom/mui';
import { KeyboardArrowDownIcon, KeyboardArrowUpIcon } from '@atom/mui/icons';
import { TaskField } from '@atom/types/task';
import { FieldFilter } from '@atom/types/work';
import { WorkTemplateTaskItem } from '@atom/types/workTemplate';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import { isCascadingField } from '@atom/utilities/workOrderFieldUtilities';

import classes from './workOrdersFilters.module.css';

const WorkOrdersTaskFieldsFilter = () => {
  const { workOrdersInputCart, filtersDisabled, dispatch } =
    useContext(WorkOrdersContext);

  const [tasks, setTasks] = useState<WorkTemplateTaskItem[]>([]);
  const [collapsed, setCollapsed] = useState<{
    [key: string]: boolean;
  }>({});

  const [getWorkOrderTemplate, { data: workOrderTemplateData }] = useLazyQuery(
    GET_WORK_ORDER_TEMPLATE,
  );

  useEffect(() => {
    if (workOrderTemplateData) {
      setTasks(workOrderTemplateData?.workOrderTemplate.tasks);
    }
  }, [workOrderTemplateData]);

  useEffect(() => {
    if (workOrdersInputCart.workTemplateIds.length === 1) {
      getWorkOrderTemplate({
        variables: {
          id: workOrdersInputCart.workTemplateIds.join(''),
        },
      });
    } else if (workOrdersInputCart.workTemplateIds.length !== 1) {
      setTasks(null);
    }
  }, [workOrdersInputCart.workTemplateIds]);

  const onCollapse = (name: string) => {
    setCollapsed({ ...collapsed, [name]: !collapsed[name] });
  };

  const onFieldFilterChange = (taskId: string, fieldFilter: FieldFilter) =>
    dispatch({
      type: WorkOrdersInputActionTypes.UPDATE_TASKS_FIELD,
      data: { taskId, fieldFilter },
    });

  const getFieldContent = (field: TaskField, task: WorkTemplateTaskItem) => (
    <div className={classes.customFieldContainer} key={field.id}>
      <TaskFieldFilterInput
        field={field}
        fieldFilter={getTaskField(workOrdersInputCart, field.id, task.id)}
        onChange={fieldFilter => onFieldFilterChange(task.id, fieldFilter)}
        disabled={filtersDisabled}
        dataCy={`workOrdersFilterTaskField-${task.name}-${field.title}`}
      />
    </div>
  );

  return (
    <>
      {(tasks || []).map(task =>
        !isNilOrEmpty(task?.fields) ? (
          <div key={task.id}>
            <div className={classes.filtersSectionHeader}>
              <div>{task.name}</div>
              <IconButton
                onClick={() => onCollapse(task.id)}
                edge="end"
                data-cy={`workOrdersFilterTaskFieldsCollapseButton-${task.name}`}
              >
                <Icon
                  icon={
                    collapsed[task.id]
                      ? KeyboardArrowDownIcon
                      : KeyboardArrowUpIcon
                  }
                />
              </IconButton>
            </div>
            {!collapsed[task.id] &&
              task.fields
                .filter(
                  field =>
                    !isCascadingField(field) &&
                    isFilterableDataType(field.dataType),
                )
                .map(field => getFieldContent(field, task))}
          </div>
        ) : (
          <div key={task.id} />
        ),
      )}
    </>
  );
};

export default WorkOrdersTaskFieldsFilter;
